import { Text } from '@mantine/core'
import type { SelfPayStatus } from '@/model/middleware-types'
import {
  PILL_STYLE_AMBER,
  PILL_STYLE_GRAY,
  PILL_STYLE_GREEN,
  PILL_STYLE_RED,
  formatCents,
  type StatusPillStyle,
} from '../utils'
import { StatusPill } from './StatusPill'

// The list endpoint ships facts (paymentStatus + payment sums); this cell derives
// the user-facing labels from them (see list-cases.contract.ts).
export type CasePayments = {
  copayPaidCents: number | null
  insurancePaidCents: number
  lastPaymentStatus: string | null
}

type PaymentsCellProps = {
  paymentStatus: string
  payments: CasePayments
  selfPayStatus?: SelfPayStatus
  /** Opens the Claim & Payment Details drawer for the row's encounter. */
  onOpen: () => void
}

function CellContent({ paymentStatus, payments }: Omit<PaymentsCellProps, 'onOpen'>) {
  switch (paymentStatus) {
    case 'failed':
      return <StatusPill style={PILL_STYLE_RED}>Payment failed</StatusPill>
    case 'paid':
      return (
        <div className="flex flex-col items-start gap-1">
          <StatusPill style={PILL_STYLE_GREEN}>
            Paid {formatCents(payments.insurancePaidCents)}
          </StatusPill>
          {payments.copayPaidCents !== null ? (
            <Text size="xs" c="green">
              Co-pay paid {formatCents(payments.copayPaidCents)}
            </Text>
          ) : null}
        </div>
      )
    case 'awaiting_payer':
      return <StatusPill style={PILL_STYLE_AMBER}>Awaiting payer</StatusPill>
    case 'ready_to_process':
      return <StatusPill style={PILL_STYLE_AMBER}>Ready to process</StatusPill>
    case 'partially_paid':
      return <StatusPill style={PILL_STYLE_AMBER}>Partially paid</StatusPill>
    case 'no_payment':
      if (payments.copayPaidCents !== null) {
        return (
          <div className="flex flex-col items-start gap-1">
            <StatusPill style={PILL_STYLE_GREEN}>
              Co-pay paid {formatCents(payments.copayPaidCents)}
            </StatusPill>
            <span className="text-sm font-medium text-orange-600">+ Insurance payment</span>
          </div>
        )
      }
      return <span className="text-sm font-medium text-blue-600">Add payment</span>
    default:
      return (
        <Text size="sm" c="dimmed">
          —
        </Text>
      )
  }
}

const SELF_PAY_PILL: Record<SelfPayStatus, { label: string; style: StatusPillStyle }> = {
  unpaid: { label: 'Self-pay unpaid', style: PILL_STYLE_RED },
  paid: { label: 'Self-pay paid', style: PILL_STYLE_GREEN },
  refunded: { label: 'Self-pay refunded', style: PILL_STYLE_GRAY },
}

export function PaymentsCell({
  paymentStatus,
  payments,
  selfPayStatus,
  onOpen,
}: PaymentsCellProps) {
  return (
    <button type="button" onClick={onOpen} className="block w-full cursor-pointer text-left">
      <div className="flex flex-col items-start gap-1">
        {selfPayStatus ? (
          <StatusPill style={SELF_PAY_PILL[selfPayStatus].style}>
            {SELF_PAY_PILL[selfPayStatus].label}
          </StatusPill>
        ) : null}
        <CellContent paymentStatus={paymentStatus} payments={payments} />
      </div>
    </button>
  )
}
